import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const Profile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [userProfile, setUserProfile] = useState(null);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'both',
    bio: '',
    skills: [],
    experienceYears: 0,
    hourlyRateMin: 0,
    hourlyRateMax: 0,
    availability: ''
  });
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get authenticated user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      // If no user session, redirect to authentication without throwing error
      if (!user || authError) {
        navigate('/authentication');
        return;
      }

      // Fetch user profile
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (profileError) throw profileError;
      setUserProfile(profile);

      // Fetch worker profile if exists
      const { data: worker, error: workerError } = await supabase?.from('worker_profiles')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();

      if (workerError && workerError?.code !== 'PGRST116') throw workerError;
      setWorkerProfile(worker);

      // Set form data
      setFormData({
        fullName: profile?.full_name || '',
        email: profile?.email || '',
        phone: profile?.phone || '',
        role: profile?.role || 'both',
        bio: worker?.bio || '',
        skills: worker?.skills || [],
        experienceYears: worker?.experience_years || 0,
        hourlyRateMin: worker?.hourly_rate_min || 0,
        hourlyRateMax: worker?.hourly_rate_max || 0,
        availability: worker?.availability || ''
      });

    } catch (err) {
      console.error('Profile error:', err);
      setError(err?.message || 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  const handleAddSkill = () => {
    if (newSkill?.trim() && !formData?.skills?.includes(newSkill?.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev?.skills, newSkill?.trim()]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev?.skills?.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update user profile
      const { error: profileError } = await supabase?.from('user_profiles')?.update({
          full_name: formData?.fullName,
          phone: formData?.phone,
          role: formData?.role,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (profileError) throw profileError;

      // Update or create worker profile if role is provider or both
      if (formData?.role === 'provider' || formData?.role === 'both') {
        const workerData = {
          user_id: user?.id,
          bio: formData?.bio,
          skills: formData?.skills,
          experience_years: parseInt(formData?.experienceYears) || 0,
          hourly_rate_min: parseFloat(formData?.hourlyRateMin) || 0,
          hourly_rate_max: parseFloat(formData?.hourlyRateMax) || 0,
          availability: formData?.availability,
          updated_at: new Date()?.toISOString()
        };

        if (workerProfile) {
          // Update existing worker profile
          const { error: workerError } = await supabase?.from('worker_profiles')?.update(workerData)?.eq('user_id', user?.id);

          if (workerError) throw workerError;
        } else {
          // Create new worker profile
          const { error: workerError } = await supabase?.from('worker_profiles')?.insert(workerData);

          if (workerError) throw workerError;
        }
      }

      setSuccess('Profile updated successfully!');
      await fetchProfileData();

    } catch (err) {
      console.error('Save error:', err);
      setError(err?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal Information', icon: 'User' },
    { id: 'professional', label: 'Professional Details', icon: 'Briefcase' },
    { id: 'verification', label: 'Verification Status', icon: 'Shield' },
    { id: 'settings', label: 'Account Settings', icon: 'Settings' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const isWorkerRole = formData?.role === 'provider' || formData?.role === 'both';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Profile - Diskarte Platform</title>
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Profile Management</h1>
          <p className="text-muted-foreground">Manage your account information and professional details</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-success">{success}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
          <div className="border-b border-border">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData?.fullName}
                    onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                    className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData?.email}
                    disabled
                    className="w-full px-4 py-2 bg-muted border border-border rounded-md text-muted-foreground cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="+63 XXX XXX XXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Account Type
                  </label>
                  <select
                    value={formData?.role}
                    onChange={(e) => handleInputChange('role', e?.target?.value)}
                    className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="seeker">Job Seeker (Client)</option>
                    <option value="provider">Service Provider (Worker)</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>
            )}

            {/* Professional Details Tab */}
            {activeTab === 'professional' && (
              <div className="space-y-6">
                {!isWorkerRole ? (
                  <div className="text-center py-8">
                    <Icon name="Briefcase" className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground mb-4">
                      Professional details are only available for Service Providers
                    </p>
                    <button
                      onClick={() => {
                        handleInputChange('role', 'both');
                        setActiveTab('personal');
                      }}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                    >
                      Change to Service Provider
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Bio
                      </label>
                      <textarea
                        value={formData?.bio}
                        onChange={(e) => handleInputChange('bio', e?.target?.value)}
                        rows={4}
                        className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Tell clients about yourself and your experience..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Skills
                      </label>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e?.target?.value)}
                          onKeyPress={(e) => e?.key === 'Enter' && handleAddSkill()}
                          className="flex-1 px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Add a skill"
                        />
                        <button
                          onClick={handleAddSkill}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                        >
                          <Icon name="Plus" className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData?.skills?.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                          >
                            {skill}
                            <button
                              onClick={() => handleRemoveSkill(skill)}
                              className="hover:text-error"
                            >
                              <Icon name="X" className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Years of Experience
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData?.experienceYears}
                        onChange={(e) => handleInputChange('experienceYears', e?.target?.value)}
                        className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Minimum Hourly Rate (₱)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData?.hourlyRateMin}
                          onChange={(e) => handleInputChange('hourlyRateMin', e?.target?.value)}
                          className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Maximum Hourly Rate (₱)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData?.hourlyRateMax}
                          onChange={(e) => handleInputChange('hourlyRateMax', e?.target?.value)}
                          className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Availability
                      </label>
                      <input
                        type="text"
                        value={formData?.availability}
                        onChange={(e) => handleInputChange('availability', e?.target?.value)}
                        className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="e.g., Monday-Friday, 9AM-5PM"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Verification Status Tab */}
            {activeTab === 'verification' && (
              <div className="space-y-6">
                <div className="bg-background rounded-lg p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Account Verification</h3>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      workerProfile?.is_verified
                        ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      {workerProfile?.is_verified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {workerProfile?.is_verified
                      ? 'Your account has been verified. You have full access to all platform features.'
                      : 'Complete your profile and submit verification documents to get verified.'}
                  </p>
                  {!workerProfile?.is_verified && (
                    <button className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
                      Submit Verification
                    </button>
                  )}
                </div>

                {workerProfile && (
                  <div className="bg-background rounded-lg p-6 border border-border">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Verification Level</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Current Level</span>
                        <span className="text-sm font-medium text-foreground capitalize">
                          {workerProfile?.verification_level || 'Basic'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Total Reviews</span>
                        <span className="text-sm font-medium text-foreground">
                          {workerProfile?.total_reviews || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Jobs Completed</span>
                        <span className="text-sm font-medium text-foreground">
                          {workerProfile?.total_jobs_completed || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Account Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-background rounded-lg p-6 border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Account Status</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Account Active</p>
                      <p className="text-xs text-muted-foreground">Your account is currently active</p>
                    </div>
                    <span className="px-3 py-1 bg-success/10 text-success rounded-full text-sm">
                      Active
                    </span>
                  </div>
                </div>

                <div className="bg-background rounded-lg p-6 border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Notification Preferences</h3>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-sm text-foreground">Email Notifications</span>
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                    </label>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-sm text-foreground">Job Application Updates</span>
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                    </label>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-sm text-foreground">Message Notifications</span>
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                    </label>
                  </div>
                </div>

                <div className="bg-error/10 rounded-lg p-6 border border-error/20">
                  <h3 className="text-lg font-semibold text-error mb-2">Danger Zone</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Once you deactivate your account, you will lose all your data. This action cannot be undone.
                  </p>
                  <button className="px-4 py-2 bg-error text-white rounded-md hover:bg-error/90">
                    Deactivate Account
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          {(activeTab === 'personal' || activeTab === 'professional') && (
            <div className="border-t border-border p-6 bg-muted/50">
              <div className="flex items-center justify-end gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-2 border border-border text-foreground rounded-md hover:bg-background transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Icon name="Loader2" className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;